/* eslint-disable react-refresh/only-export-components -- context provider dan hook adalah satu API publik. */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { CartItem, CartState, Identifier, Product } from "../lib/types";

const CART_KEY = "sedapsayur_cart_v1";

export interface ReconcileResult {
  removed: string[];
  updated: string[];
}

interface CartContextValue extends CartState {
  itemCount: number;
  subtotal: number;
  addItem: (product: Product, branchId: Identifier) => void;
  setQuantity: (productId: Identifier, quantity: number) => void;
  removeItem: (productId: Identifier) => void;
  clearCart: () => void;
  reconcileBranch: (branchId: Identifier, products: Product[]) => ReconcileResult;
  replaceItems: (branchId: Identifier, items: CartItem[]) => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

function readCart(): CartState {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw) as Partial<CartState>;
    return {
      branchId: parsed.branchId,
      items: Array.isArray(parsed.items) ? parsed.items : [],
    };
  } catch {
    return { items: [] };
  }
}

const currentPrice = (item: CartItem): number => item.product.promoPrice ?? item.product.price;

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartState>(readCart);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const addItem = (product: Product, branchId: Identifier) => {
    setCart((current) => {
      if (current.branchId !== undefined && String(current.branchId) !== String(branchId) && current.items.length > 0) {
        return current;
      }
      const existing = current.items.find((item) => String(item.product.id) === String(product.id));
      const max = product.isUnlimited ? 99 : Math.max(0, product.stock);
      const nextItems = existing
        ? current.items.map((item) =>
            String(item.product.id) === String(product.id)
              ? { ...item, product, quantity: Math.min(item.quantity + 1, max) }
              : item,
          )
        : [...current.items, { product, quantity: Math.min(1, max) }];
      return { branchId, items: nextItems.filter((item) => item.quantity > 0) };
    });
  };

  const setQuantity = (productId: Identifier, quantity: number) => {
    setCart((current) => ({
      ...current,
      items: current.items
        .map((item) =>
          String(item.product.id) === String(productId)
            ? {
                ...item,
                quantity: Math.min(
                  Math.max(0, quantity),
                  item.product.isUnlimited ? 99 : item.product.stock,
                ),
              }
            : item,
        )
        .filter((item) => item.quantity > 0),
    }));
  };

  const removeItem = (productId: Identifier) => {
    setCart((current) => ({
      ...current,
      items: current.items.filter((item) => String(item.product.id) !== String(productId)),
    }));
  };

  const clearCart = () => setCart({ items: [] });

  const reconcileBranch = (branchId: Identifier, products: Product[]): ReconcileResult => {
    const removed: string[] = [];
    const updated: string[] = [];
    setCart((current) => {
      const items = current.items.flatMap((item) => {
        const latest = products.find((product) => String(product.id) === String(item.product.id));
        if (
          !latest ||
          !latest.isAvailable ||
          (!latest.isUnlimited && latest.stock < 1)
        ) {
          removed.push(item.product.name);
          return [];
        }
        const quantity = Math.min(
          item.quantity,
          latest.isUnlimited ? 99 : latest.stock,
        );
        if (currentPrice(item) !== (latest.promoPrice ?? latest.price) || quantity !== item.quantity) {
          updated.push(item.product.name);
        }
        return [{ product: latest, quantity }];
      });
      return { branchId, items };
    });
    return { removed, updated };
  };

  const replaceItems = (branchId: Identifier, items: CartItem[]) => setCart({ branchId, items });

  const itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = cart.items.reduce((total, item) => total + currentPrice(item) * item.quantity, 0);

  const value = useMemo<CartContextValue>(
    () => ({
      ...cart,
      itemCount,
      subtotal,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
      reconcileBranch,
      replaceItems,
    }),
    [cart, itemCount, subtotal],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart harus digunakan di dalam CartProvider");
  return context;
}
